import type { KeyboardEvent, MouseEvent } from 'react';

/**
 * Spreadsheet-style keys for the buyer board's quantity column: the buyer
 * types over a quantity and Enter (or an arrow) takes them to the next line's
 * quantity, the way Gains and Excel work.
 */

export type QuantityKeyIntent = 'next' | 'prev' | 'revert';

interface KeyLike {
  key: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  nativeEvent: { isComposing: boolean };
}

/** Marks a line's quantity input; the value is the line's suggestion id. */
export const LINE_QUANTITY_ATTRIBUTE = 'data-line-quantity';

/**
 * Marks a need row's total (GEN-2938) with its SO lines' suggestion ids,
 * space-separated: a save on any of them disables the total too.
 */
export const NEED_MEMBERS_ATTRIBUTE = 'data-need-members';

/** What a keydown in a quantity input means, or null to leave it alone. */
export function quantityKeyIntent(e: KeyLike): QuantityKeyIntent | null {
  // Shortcuts and IME composition own their keys.
  if (e.ctrlKey || e.metaKey || e.altKey || e.nativeEvent.isComposing) {
    return null;
  }
  switch (e.key) {
    case 'Enter':
      return e.shiftKey ? 'prev' : 'next';
    // Shift+arrows keep extending the text selection.
    case 'ArrowDown':
      return e.shiftKey ? null : 'next';
    case 'ArrowUp':
      return e.shiftKey ? null : 'prev';
    case 'Escape':
      return 'revert';
    default:
      return null;
  }
}

function isSpacerRow(row: Element): boolean {
  return row.getAttribute('aria-hidden') === 'true';
}

/**
 * The quantity input of the nearest drawn row above or below `from`'s row that
 * takes an edit, or null. Rows without one (group headers, stock and trashed
 * lines) are passed over, as are lines locked by a save and those in `skip`.
 * A virtualized body's spacer ends the search: the rows behind it are not
 * drawn, so whatever lies past it is not the adjacent line.
 */
export function adjacentQuantityInput(
  from: Element,
  direction: 'next' | 'prev',
  skip: ReadonlySet<string> = new Set(),
): HTMLInputElement | null {
  const step = (row: Element) =>
    direction === 'next' ? row.nextElementSibling : row.previousElementSibling;
  const origin = from.closest('tr');
  for (
    let row = origin === null ? null : step(origin);
    row !== null && !isSpacerRow(row);
    row = step(row)
  ) {
    const input = row.querySelector<HTMLInputElement>(
      `input[${LINE_QUANTITY_ATTRIBUTE}]:not(:disabled)`,
    );
    const suggestionId = input?.getAttribute(LINE_QUANTITY_ATTRIBUTE);
    if (input && suggestionId != null && !isLocked(input, suggestionId, skip)) {
      return input;
    }
  }
  return null;
}

// A need total locks with its SO lines, so a save on one of them skips it too.
function isLocked(
  input: HTMLInputElement,
  suggestionId: string,
  skip: ReadonlySet<string>,
): boolean {
  if (skip.has(suggestionId)) return true;
  const members = input.getAttribute(NEED_MEMBERS_ATTRIBUTE)?.split(' ') ?? [];
  return members.some((member) => skip.has(member));
}

/**
 * The quantity column's keydown: Enter and the arrows move to the adjacent
 * line's quantity, passing over the lines `locking` names (leaving this input
 * saves it, and a save locks them); with nowhere to go, Enter still saves by
 * leaving the input. Escape drops the edit through `revert` and keeps the
 * cell, selected so the buyer can type again.
 */
export function handleQuantityKeyDown(
  event: KeyboardEvent<HTMLInputElement>,
  {
    revert,
    locking,
  }: { revert: () => void; locking: () => ReadonlySet<string> },
): void {
  const intent = quantityKeyIntent(event);
  if (intent == null) return;
  event.preventDefault();
  const input = event.currentTarget;
  if (intent === 'revert') {
    revert();
    // Select once the saved quantity is back in the input: writing the value
    // moves the caret to the end.
    requestAnimationFrame(() => input.select());
    return;
  }
  const target = adjacentQuantityInput(input, intent, locking());
  if (target !== null) focusQuantityInput(target);
  else if (event.key === 'Enter') input.blur();
}

/**
 * Moves focus to `target`, scrolling only as far as needed to show it. Leaving
 * the current input blurs it, which is what saves its quantity.
 */
export function focusQuantityInput(target: HTMLInputElement): void {
  target.focus({ preventScroll: true });
  target.scrollIntoView({ block: 'nearest' });
}

/**
 * A typed quantity kept to digits and one decimal point, or null to refuse the
 * keystroke (a second point) and keep the draft as it was.
 */
export function quantityDraft(value: string): string | null {
  const draft = value.replace(/[^\d.]/g, '');
  return draft.indexOf('.') === draft.lastIndexOf('.') ? draft : null;
}

/**
 * The first click into the quantity selects all of it, so the buyer can type
 * over it. The browser's mousedown would place the caret at the pointer;
 * preventing it and taking focus here keeps the selection the focus handler
 * makes. A click into the focused input places the caret as usual.
 */
export function selectOnFirstClick(event: MouseEvent<HTMLInputElement>) {
  const input = event.currentTarget;
  if (event.button !== 0 || input.ownerDocument.activeElement === input) {
    return;
  }
  event.preventDefault();
  input.focus();
}
